import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import BarChartIcon from "@mui/icons-material/BarChart";
import { Box, Button, FormControlLabel, IconButton, Switch, Typography } from "@mui/material";
import { hasCircularRecipeIngredient } from "@notemeal/recipe-utils";
import { useHasFeature } from "shared-ui/Feature";
import { inputToNumber } from "shared-ui/utils/inputUtils";
import { trackEvent } from "apps/web/src/reporting/reporting";
import React, { Dispatch, useState } from "react";
import { FullServingAmountFragment, RecipeWithServingsFragment } from "../../../types";
import { useSnackbar } from "../../Snackbar/SnackbarContext";
import { useIsFoodFormRedesign } from "../../FoodFormUi/FoodFormUiContext";
import { ServingAmountsEditChipListWithSearchBar } from "../../ServingAmounts/Edit/ChipListWithSearchBar";
import { NewChip } from "../../universal/NewChip";
import { canSaveRecipeFormTooltips, formValidationMessages, RecipeFormAction, RecipeFormState } from "./utils";

interface IngredientsProps {
  state: RecipeFormState;
  dispatch: Dispatch<RecipeFormAction>;
  setNutrientsOpen: (value: boolean) => void;
  disabled?: boolean;
  onAiGenerateClicked: null | (() => void);
  onAiTranscribeClicked?: () => void;
  forStaff?: boolean;
  openScaleRecipe?: () => void;
}

export const Ingredients = ({
  state,
  dispatch,
  setNutrientsOpen,
  disabled,
  onAiGenerateClicked,
  onAiTranscribeClicked,
  forStaff,
  openScaleRecipe,
}: IngredientsProps) => {
  const isRedesign = useIsFoodFormRedesign();
  const canSaveTooltips = canSaveRecipeFormTooltips(state);
  const hasRecipeScaling = useHasFeature("performanceKitchen");
  const { setMessage } = useSnackbar();
  const [displayMacros, setDisplayMacros] = useState(true);

  const handleChangeIngredients = (servingAmounts: readonly FullServingAmountFragment[], recipe?: RecipeWithServingsFragment) => {
    if (hasCircularRecipeIngredient(state.id, servingAmounts, recipe)) {
      setMessage("error", "A recipe cannot be added to itself.");
      return;
    }
    dispatch({
      type: "CHANGE_INGREDIENTS",
      value: servingAmounts,
    });
  };

  const isValidIngredients = canSaveTooltips.includes(formValidationMessages.INGREDIENTS) && state.saveClicked;

  return (
    <Box sx={{ gap: 2, display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h4">Ingredients</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isRedesign && (
            <FormControlLabel
              label="Display macros"
              control={
                <Switch disabled={disabled} size="small" checked={displayMacros} onChange={(_, checked) => setDisplayMacros(checked)} />
              }
              sx={{ m: 0, gap: 1 }}
            />
          )}
          <IconButton
            onClick={() => setNutrientsOpen(true)}
            aria-label="Open nutrient analysis"
            sx={
              isRedesign
                ? {
                    width: 36,
                    height: 36,
                    border: 1,
                    borderColor: "greyscale.300",
                    borderRadius: 1,
                    p: 0,
                  }
                : undefined
            }
          >
            <BarChartIcon sx={{ color: "accents.yellow.400" }} />
          </IconButton>
          {isRedesign && (hasRecipeScaling || forStaff) && openScaleRecipe && (
            <Button
              variant="outlined"
              disabled={disabled || state.ingredients.length === 0 || !inputToNumber(state.serving.perRecipeYieldInput)}
              onClick={() => {
                openScaleRecipe();
                trackEvent("Nutrition | Team | Recipe | Scale Recipe", {});
              }}
            >
              Scale Recipe
              <NewChip feature="scalingRecipe" />
            </Button>
          )}
        </Box>
      </Box>
      {isValidIngredients && (
        <Typography variant="body2" color="error">
          Please add at least one ingredient
        </Typography>
      )}
      <ServingAmountsEditChipListWithSearchBar
        disabled={disabled}
        enableBranded={true}
        selectedServingAmounts={state.ingredients}
        selected
        noCache
        includeRecipeIngredients
        displayMacros={displayMacros}
        onChange={handleChangeIngredients}
        sx={{ overflowY: "auto" }}
      />
      <Box sx={{ display: "flex", gap: 2 }}>
        {onAiGenerateClicked && (
          <Button variant="outlined" fullWidth onClick={() => onAiGenerateClicked()}>
            <AutoAwesomeIcon sx={{ fontSize: 16, marginRight: 1 }} />
            AI Recipe Generator
          </Button>
        )}
        {onAiTranscribeClicked && (
          <Button variant="outlined" fullWidth onClick={() => onAiTranscribeClicked()}>
            <AutoStoriesIcon sx={{ fontSize: 16, marginRight: 1 }} />
            AI Image Transcriber
            <NewChip feature="transcribeRecipe" />
          </Button>
        )}
      </Box>
    </Box>
  );
};
